<template>
    <div class="calc-step2">
        <CalcCard
            step="§calc.step2.eyebrow"
            title="§calc.step2.title"
            subtitle="§calc.step2.subtitle"
        >
            <div v-if="showStep2Notice" class="calc-step2-notice">
                <p class="calc-step2-notice__heading">{{ $t('§calc.step2.notice.heading') }}</p>
                <ul class="calc-step2-notice__list">
                    <li v-for="key in residualErrorKeys" :key="key">{{ $t(key) }}</li>
                </ul>
            </div>

            <CalcField full label="§calc.step2.fields.country">
                <CalcDropdown
                    :model-value="data.country"
                    :options="countryOptions"
                    @update:model-value="emitField('country', $event)"
                >
                    <template #value="slotProps">
                        <span class="calc-step2__country-value">
                            <CalcFlagIcon
                                v-if="slotProps.value && slotProps.value.value"
                                :code="slotProps.value.value"
                            />
                            <span>{{ selectedCountryTranslated }}</span>
                        </span>
                    </template>

                    <template #option="slotProps">
                        <span class="calc-step2__country-option">
                            <CalcFlagIcon :code="slotProps.option.value" />
                            <span>{{ slotProps.option.label }}</span>
                        </span>
                    </template>
                </CalcDropdown>
            </CalcField>

            <div class="calc-step2__spacer-28" />

            <div class="calc-toggle-row">
                <span class="calc-toggle-row__label">
                    {{ $t('§calc.step2.fields.depreciation') }}
                </span>
                <CalcToggle
                    :model-value="!!data.depreciation"
                    @update:model-value="emitField('depreciation', $event)"
                />
            </div>

            <div class="calc-step2__spacer-22" />

            <CalcField label="§calc.step2.fields.residual">
                <template #info>
                    <CalcTooltip>
                        {{ $t('§calc.step2.fields.residual_tooltip') }}
                    </CalcTooltip>
                </template>

                <CalcStringInput
                    v-if="data.depreciation"
                    :model-value="depreciatedResidualDisplay"
                    locked
                />
                <CalcNumberInput
                    v-else
                    :model-value="data.residual"
                    placeholder="§calc.step2.fields.residual_placeholder"
                    @update:model-value="emitField('residual', $event)"
                />

                <p v-if="!data.depreciation" class="calc-field-note">
                    <span class="calc-field-note__label">
                        {{ $t('§calc.step2.fields.residual_note_label') }}
                    </span>
                    {{ $t('§calc.step2.fields.residual_note') }}
                </p>
            </CalcField>

            <div class="calc-btn-row calc-btn-row--end calc-step2__footer">
                <CalcButton
                    variant="secondary"
                    label="§calc.step2.cta.back"
                    @click="$emit('back')"
                />
                <CalcButton
                    variant="primary"
                    label="§calc.step2.cta.calculate"
                    icon-name="arrow-right"
                    icon-color="white"
                    :disabled="!residualValid"
                    @click="onCalculate"
                />
            </div>
        </CalcCard>

        <CalcDisclaimer />
    </div>
</template>

<script>
import CalcCard from '../components/CalcCard.vue'
import CalcField from '../components/CalcField.vue'
import CalcDropdown from '../components/CalcDropdown.vue'
import CalcStringInput from '../components/CalcStringInput.vue'
import CalcNumberInput from '../components/CalcNumberInput.vue'
import CalcToggle from '../components/CalcToggle.vue'
import CalcTooltip from '../components/CalcTooltip.vue'
import CalcFlagIcon from '../components/CalcFlagIcon.vue'
import CalcButton from '../components/CalcButton.vue'
import CalcDisclaimer from '../components/CalcDisclaimer.vue'
import { RESIDUAL_MAX } from '../services/prefill.js'
import { FULLY_DEPRECIATED_RESIDUAL } from '../services/calcParams.js'

function getFinancingCountries() {
    const list = (typeof window !== 'undefined' && window.calcFinancingCountries) || []
    return Array.isArray(list) ? list : []
}

export default {
    name: 'CalcStep2',

    components: {
        CalcCard,
        CalcField,
        CalcDropdown,
        CalcStringInput,
        CalcNumberInput,
        CalcToggle,
        CalcTooltip,
        CalcFlagIcon,
        CalcButton,
        CalcDisclaimer,
    },

    props: {
        data: {
            type: Object,
            required: true,
        },
        selectedCountryTranslated: {
            type: String,
            default: '',
        },
    },

    emits: ['update:data', 'back', 'calculate'],

    computed: {
        depreciatedResidualDisplay() {
            return this.displayAsCurrency(FULLY_DEPRECIATED_RESIDUAL, 0)
        },

        countryOptions() {
            const collator = new Intl.Collator(this.$i18n.locale, { sensitivity: 'base' })
            return getFinancingCountries()
                .map(({ code, name, translationKey }) => {
                    const labelKey = translationKey
                    const translated = labelKey ? this.$t(labelKey) : null
                    const displayed =
                        translated && translated !== labelKey
                            ? translated
                            : (name || code)
                    return {
                        value: code,
                        label: labelKey || name || code,
                        displayed,
                    }
                })
                .sort((a, b) => collator.compare(a.displayed, b.displayed))
                .map(({ value, label }) => ({ value, label }))
        },

        residualErrorKeys() {
            if (this.data.depreciation) return []
            const r = this.data.residual
            const a = this.data.amount
            const out = []
            if (r == null || !Number.isFinite(r)) out.push('§calc.step2.notice.residual_required')
            else {
                if (r <= 0) out.push('§calc.step2.notice.residual_not_positive')
                if (typeof a === 'number' && Number.isFinite(a) && r > a) out.push('§calc.step2.notice.residual_too_large_vs_amount')
                if (r > RESIDUAL_MAX) out.push('§calc.step2.notice.residual_above_max')
            }
            return out
        },

        residualValid() {
            return this.residualErrorKeys.length === 0
        },

        showStep2Notice() {
            if (this.data.depreciation) return false
            return this.residualErrorKeys.length > 0 && this.data.residual != null
        },
    },

    methods: {
        emitField(name, value) {
            this.$emit('update:data', { [name]: value })
        },

        onCalculate() {
            if (!this.residualValid) return
            this.$emit('calculate')
        },
    },
}
</script>

<style scoped>
.calc-step2 {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
}

.calc-step2__spacer-28 {
    height: 28px;
}

.calc-step2__spacer-22 {
    height: 22px;
}

.calc-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 4px 0;
}

.calc-toggle-row__label {
    font-family: 'TWK Lausanne', 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: var(--calc-ink);
}

.calc-field-note {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.45;
    color: var(--calc-ink);
    opacity: 0.5;
}

.calc-field-note__label {
    font-weight: 600;
    opacity: 1;
    color: var(--calc-ink);
}

.calc-step2__footer {
    margin-top: 28px;
}

.calc-step2__country-value,
.calc-step2__country-option {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.calc-step2-notice {
    margin: 0 0 24px;
    background: var(--calc-green-mint);
    border-radius: 12px;
    padding: 16px 22px;
    color: var(--calc-ink);
}

.calc-step2-notice__heading {
    font-weight: 600;
    margin: 0 0 8px;
    font-size: 16px;
}

.calc-step2-notice__list {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 1.5;
}
</style>
